interface Position {
  x: number;
  y: number;
}

interface ContentBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface CanvasSize {
  width: number;
  height: number;
}

interface ClampPanOptions {
  contentBounds: ContentBounds | null;
  canvasSize: CanvasSize;
  zoom: number;
  margin: number;
}

/**
 * Clamps a pan offset so the tree content stays within `margin` world px of
 * going fully off-screen. The pan → view-center mapping mirrors
 * useCanvasRendering: center.x = canvasSize.width/2 - pan.x, center.y = 80 - pan.y.
 */
export const clampPan = (pan: Position, { contentBounds, canvasSize, zoom, margin }: ClampPanOptions): Position => {
  if (!contentBounds || canvasSize.width === 0 || canvasSize.height === 0 || zoom <= 0) {
    return pan;
  }

  const { minX, maxX, minY, maxY } = contentBounds;
  const halfViewW = canvasSize.width / (2 * zoom);
  const halfViewH = canvasSize.height / (2 * zoom);

  const centerX = canvasSize.width / 2 - pan.x;
  const centerY = 80 - pan.y;

  const clampedCenterX = clampCenter(centerX, minX, maxX, halfViewW, margin);
  const clampedCenterY = clampCenter(centerY, minY, maxY, halfViewH, margin);

  return {
    x: canvasSize.width / 2 - clampedCenterX,
    y: 80 - clampedCenterY,
  };
};

const clampCenter = (center: number, min: number, max: number, halfView: number, margin: number): number => {
  const lowerBound = min - halfView + margin;
  const upperBound = max + halfView - margin;

  if (lowerBound > upperBound) {
    return (min + max) / 2;
  }

  return Math.min(Math.max(center, lowerBound), upperBound);
};
